<template>
  <div>
    <div v-for="(statistic, indexStat) in statistics" :key="indexStat" class="histogram-horizontal">
      <div v-if="statistic.min!==null && statistic.max!==null" class="minmax">{{ statistic.min }} .. {{ statistic.max }}</div>
      <div v-else-if="statistic.min===null && statistic.max!==null" class="minmax">&lt; {{ statistic.max }}</div>
      <div v-else-if="statistic.min!==null && statistic.max===null" class="minmax">&gt; {{ statistic.min }}</div>

      <div class="progress" @click="goToSearchGatewayForm(statistic.min, statistic.max)">
        <div :class="statistic.color" :style="{width:statistic.valuePercentage+'%'}" class="progress-bar" />
      </div>

      <div class="value">{{ statistic.value }}</div>
    </div>
  </div>
</template>

<script>
import { camelCase, flatMap, map, cloneDeep } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import constants from '@/configuration/constants'

export default {
  props: {
    // Update time, used to trigger and update from parent
    update: {
      type: Number,
      required: true
    },
    // Total of gateways to calculate %
    totalCount: {
      type: Number,
      required: true
    },
    // Attribute to look for values in lastEventsCounters
    attribute: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      // Array of empty objects to display empty bars instead of nothing
      statistics: [{}, {}, {}, {}, {}]
    }
  },

  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(['selectedFleet', 'dashboardSettings'])
  },

  watch: {
    // When selectedFleet change
    update: 'getFleetGatewaysEquipmentsStatistics'
  },

  methods: {
    async getFleetGatewaysEquipmentsStatistics () {
      const attribute = camelCase(`fleet-${this.attribute}-distribution`)
      const attributeSettings = this.dashboardSettings[attribute] || constants[attribute]
      const ranges = flatMap(attributeSettings, (range) => { return `${range.start},${range.end}` }).join(';')
      const colors = flatMap(attributeSettings, (range) => { return range.color })

      if (this.totalCount > 0) {
        let error, response
        [error, response] = await to(this.$api.getFleetLastStatistics(this.selectedFleet, { metricNames: this.attribute, ranges }))
        if (error || !response) return

        this.statistics = response.data.list[0].numericStatistics

        // Sort stats
        this.statistics = this.statistics.sort((a, b) => {
          if (a.min === null) {
            // If min is null > first
            return -1
          } else if (a.max === null) {
            // If max is null > last
            return 1
          } else if (a.min === b.min) {
            return 0
          }
          // Else, sort by min asc
          return a.min < b.min ? -1 : 1
        })

        // Calculate percentages and add colors
        map(this.statistics, (statistic, index) => {
          statistic.valuePercentage = statistic.value / this.totalCount * 100
          statistic.color = 'progress-' + colors[index]
        })
      } else {
        // Empty stats
        this.statistics = cloneDeep(attributeSettings)
        map(this.statistics, (stat) => {
          stat.min = stat.start
          stat.max = stat.end
          stat.value = 0
          stat.valuePercentage = 0
        })
      }
    },

    goToSearchGatewayForm (min = '', max = '') {
      const filters = { values: {} }
      filters.values[this.attribute] = { from: min, to: max }
      this.$store.commit('setGatewaysFilters', filters)

      this.$router.push('/gateways')
    }
  }
}
</script>

<style scoped lang="scss">
  .histogram-horizontal {
    display: flex;
    align-items: center;
    height: 30px;

    .minmax {
      flex: 2;
    }

    .value {
      flex: 1;
      text-align: right;
    }

    .progress {
      flex: 5;
      height: 28px;
      cursor: pointer;
      border-radius: 0;

      .progress-bar {
        height: 16px;
        margin-top: 6px;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        transition-timing-function: ease-in;
        transition-duration: 0.2s;
        transition-property: all;
      }

      &:hover {
        .progress-bar {
          height: 22px;
          margin-top: 3px;
        }
      }
    }
  }
</style>
